import React from 'react';
import { motion } from 'framer-motion';
import { Users, Clock, Target, Heart } from 'lucide-react';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import SEO from '../components/SEO';

const About = () => {
  const values = [
    { icon: Clock, title: 'Responsiveness', description: 'Immediate action on every enquiry with 24/7/365 availability' },
    { icon: Target, title: 'Reliability', description: 'On-time delivery backed by decades of logistics excellence' },
    { icon: Users, title: 'Partnership', description: 'Dedicated account management building long-term relationships' },
    { icon: Heart, title: 'Integrity', description: 'Transparent communication and honest service every time' },
  ];

  const milestones = [
    { year: '2000+', label: 'AOG Movements Annually' },
    { year: '150+', label: 'Countries Served' },
    { year: '24/7', label: 'Operations Centre' },
    { year: '99.8%', label: 'On-Time Performance' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO title="About Aero Time Critical" description="Aero Time Critical is a global freight-forwarding partner specialising in time-critical, AOG and aerospace logistics with 24/7 operational support." path="/about" />
      <PageHero
        badge="ABOUT US"
        title="About"
        titleAccent="Aero Time Critical"
        description="A global freight-forwarding partner specialising in time-critical, AOG and urgent aerospace shipments with complete control from collection through to final delivery."
        image="https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/esv8b4gm_AOG%20ATC%20.png"
      />

      {/* Our Story */}
      <section className="bg-[#F7F9FB] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/am9t13d4_On%20Board%20Courier.jpg"
                alt="Aero Time Critical courier managing an urgent consignment"
                className="w-full h-[480px] object-cover rounded-lg shadow-xl"
                loading="lazy"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#0089D0] font-semibold tracking-wide uppercase text-sm">Our Mission</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-3 mb-8 text-[#003057]">
                When Time Can't Wait, We Deliver
              </h2>
              <div className="space-y-6 text-lg text-[#4A4A4A] leading-relaxed">
                <p>
                  Aero Time Critical Ltd delivers responsive, reliable logistics solutions for organisations requiring time-definite delivery and specialist freight expertise.
                </p>
                <p>
                  From routine global air and sea freight to urgent aircraft-on-ground (AOG) situations, we combine aerospace logistics knowledge with responsive service to keep your operations moving.
                </p>
                <p>
                  Based in Slough near Heathrow Airport, our 24/7 operations team coordinates air, road and sea movements across all major trade lanes with proactive milestone updates and dedicated project management.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#0089D0] font-semibold tracking-wide uppercase text-sm">What Drives Us</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-3 mb-6 text-[#003057]">Our Values</h2>
            <p className="text-lg text-[#4A4A4A] max-w-3xl mx-auto">
              The principles that guide every shipment we handle
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="service-card text-center"
                >
                  <div className="w-14 h-14 bg-[#0089D0]/10 rounded-lg flex items-center justify-center mx-auto mb-6">
                    <Icon className="text-[#0089D0]" size={28} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-[#003057]">{value.title}</h3>
                  <p className="text-[#4A4A4A] leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="bg-[#003057] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white">By the Numbers</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl md:text-6xl font-bold text-[#C9A227] mb-4">
                  {milestone.year}
                </div>
                <div className="text-gray-300">{milestone.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to Work Together?"
        description="Contact our team to discuss your freight requirements"
        buttonText="Get a Quote"
      />
    </div>
  );
};

export default About;
